<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "#imports";
import { useAuthBootstrap } from "~/composables/useAuthBootstrap";
import { useFirstOrderDiscount } from "~/composables/useFirstOrderDiscount";

const route = useRoute();
const { isAuthorized, hasCheckedBootstrap } = useAuthBootstrap();
const { hasOrders, client, refresh, reset } = useFirstOrderDiscount();
const mounted = ref(false);
const dismissed = ref(false);
watch(client, () => {
  dismissed.value = false;
});

const dismiss = () => {
  dismissed.value = true;
};

const checkAvailability = () => {
  if (!mounted.value) return;
  if (!isAuthorized.value) {
    reset();
    return;
  }
  if (hasCheckedBootstrap.value) void refresh();
};

watch(
  [mounted, isAuthorized, hasCheckedBootstrap, () => route.fullPath],
  checkAvailability,
);
onMounted(() => {
  mounted.value = true;
  window.addEventListener("focus", checkAvailability);
});
onBeforeUnmount(() => window.removeEventListener("focus", checkAvailability));
</script>

<template>
  <div
    v-if="
      mounted &&
      isAuthorized &&
      hasCheckedBootstrap &&
      hasOrders === false &&
      !dismissed
    "
    class="h-[64px] shrink-0 md:h-[56px]"
  >
    <aside
      class="first-order-discount fixed inset-x-0 top-[var(--layout-mobile-header-height)] z-[1001] flex h-[64px] items-center gap-3 px-[var(--page-gutter)] py-2 md:h-[56px] md:gap-4 lg:left-[var(--layout-sidebar-width)] lg:top-[var(--layout-header-height)]"
      aria-label="Скидка на первый заказ"
    >
      <span
        class="first-order-discount__badge shrink-0 rounded-xl px-3 py-2 text-[15px] leading-none font-bold tracking-tight"
        aria-hidden="true"
        >−10%</span
      >
      <p class="min-w-0 flex-1 text-[13px] leading-snug md:text-[14px]">
        Вам доступна
        <strong class="first-order-discount__highlight font-bold"
          >скидка 10%</strong
        >
        на первый заказ
      </p>
      <button
        type="button"
        class="first-order-discount__close flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
        aria-label="Скрыть уведомление о скидке на первый заказ"
        title="Скрыть уведомление"
        @click="dismiss"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </aside>
  </div>
</template>

<style scoped>
.first-order-discount {
  color: var(--color-gray-ink);
  background: linear-gradient(
    110deg,
    var(--color-selected-filter) 0%,
    var(--color-white-card) 100%
  );
  border-top: 1px solid var(--color-white);
  border-bottom: 1px solid rgb(var(--color-orange-rgb) / 0.14);
  box-shadow: 0 3px 12px rgb(var(--color-gray-dark-rgb) / 0.035);
}

.first-order-discount__badge {
  color: var(--color-white);
  background: var(--color-orange);
  border: 1px solid var(--color-orange);
  box-shadow: inset 0 1px 0 rgb(var(--color-white-rgb) / 0.14);
}

.first-order-discount__highlight {
  color: var(--color-orange);
}

.first-order-discount__close {
  color: var(--color-gray-subtle);
  background: rgb(var(--color-white-rgb) / 0.6);
  border: 1px solid rgb(var(--color-orange-rgb) / 0.12);
  outline-color: var(--color-orange);
}

.first-order-discount__close:hover {
  color: var(--color-orange);
  background: var(--color-white);
  border-color: rgb(var(--color-orange-rgb) / 0.3);
}
</style>
